<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { cn } from '@/lib/utils'

interface Props {
  class?: HTMLAttributes['class']
  /** Unique ID for SVG defs (needed when multiple bubbles on page) */
  id?: string
  /** Whether to show the diamond ornament at top */
  showDiamond?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  id: 'bubble',
  showDiamond: true
})

// Use a unique ID for SVG defs to prevent conflicts
const uniqueId = computed(() => `${props.id}-${Math.random().toString(36).slice(2, 9)}`)

// Container ref for measuring actual size
const containerRef = ref<HTMLDivElement | null>(null)
const measuredWidth = ref(48)
const measuredHeight = ref(48)

// Observe size changes
let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  if (containerRef.value) {
    // Initial measurement
    measuredWidth.value = containerRef.value.offsetWidth
    measuredHeight.value = containerRef.value.offsetHeight
    
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        measuredWidth.value = entry.contentRect.width
        measuredHeight.value = entry.contentRect.height
      }
    })
    resizeObserver.observe(containerRef.value)
  }
})

onUnmounted(() => {
  resizeObserver?.disconnect()
})

// Design constants - scale chamfer based on size
const chamferRatio = 0.15 // 15% of the smaller dimension
const minChamfer = 8
const maxChamfer = 16
const strokeWidth = 2
const diamondSize = 10

// Calculate chamfer based on current size
const chamfer = computed(() => {
  const minDim = Math.min(measuredWidth.value, measuredHeight.value)
  return Math.max(minChamfer, Math.min(maxChamfer, minDim * chamferRatio))
})

// Calculate frame path with 45° chamfered corners
const framePath = computed(() => {
  const w = measuredWidth.value
  const h = measuredHeight.value
  const c = chamfer.value
  const cx = w / 2
  const gap = props.showDiamond ? diamondSize + 4 : 0
  
  if (props.showDiamond && w > 100) {
    // Path with gap for diamond (expanded state)
    return `
      M ${cx - gap} 0
      L ${c} 0
      L 0 ${c}
      L 0 ${h - c}
      L ${c} ${h}
      L ${w - c} ${h}
      L ${w} ${h - c}
      L ${w} ${c}
      L ${w - c} 0
      L ${cx + gap} 0
    `
  } else {
    // Closed path without gap (collapsed state)
    return `
      M ${c} 0
      L ${w - c} 0
      L ${w} ${c}
      L ${w} ${h - c}
      L ${w - c} ${h}
      L ${c} ${h}
      L 0 ${h - c}
      L 0 ${c}
      Z
    `
  }
})

// Diamond ornament path
const diamondPath = computed(() => {
  const cx = measuredWidth.value / 2
  const d = diamondSize
  const topOffset = -d * 0.6
  
  return `
    M ${cx} ${topOffset}
    L ${cx + d} ${d * 0.6}
    L ${cx} ${d * 1.8}
    L ${cx - d} ${d * 0.6}
    Z
  `
})

// Inner diamond fill
const innerDiamondPath = computed(() => {
  const cx = measuredWidth.value / 2
  const d = diamondSize * 0.5
  const topOffset = diamondSize * 0.1
  
  return `
    M ${cx} ${topOffset}
    L ${cx + d} ${d + topOffset}
    L ${cx} ${d * 2 + topOffset}
    L ${cx - d} ${d + topOffset}
    Z
  `
})

// Background fill path (always closed)
const backgroundPath = computed(() => {
  const w = measuredWidth.value
  const h = measuredHeight.value
  const c = chamfer.value
  
  return `
    M 0 ${c}
    L ${c} 0
    L ${w - c} 0
    L ${w} ${c}
    L ${w} ${h - c}
    L ${w - c} ${h}
    L ${c} ${h}
    L 0 ${h - c}
    Z
  `
})

// Whether to show diamond (only when expanded enough)
const shouldShowDiamond = computed(() => props.showDiamond && measuredWidth.value > 100)
</script>

<template>
  <div
    ref="containerRef"
    :class="cn('bubble-frame-container absolute inset-0', props.class)"
  >
    <svg
      class="bubble-frame absolute inset-0 w-full h-full"
      :viewBox="`0 0 ${measuredWidth} ${measuredHeight}`"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <!-- Gold gradient -->
        <linearGradient :id="`${uniqueId}-gold`" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="var(--gold-start)" />
          <stop offset="50%" stop-color="var(--gold-mid)" />
          <stop offset="100%" stop-color="var(--gold-end)" />
        </linearGradient>
        
        <!-- Glow filter -->
        <filter :id="`${uniqueId}-glow`" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      
      <!-- Background fill -->
      <path
        :d="backgroundPath"
        fill="hsl(var(--card))"
      />
      
      <!-- Main border frame -->
      <path
        :d="framePath"
        fill="none"
        :stroke="`url(#${uniqueId}-gold)`"
        :stroke-width="strokeWidth"
        stroke-linecap="square"
        stroke-linejoin="miter"
        :filter="`url(#${uniqueId}-glow)`"
      />
      
      <!-- Top diamond ornament (only when expanded) -->
      <g v-if="shouldShowDiamond" class="diamond-ornament">
        <path
          :d="diamondPath"
          fill="hsl(var(--card))"
          :stroke="`url(#${uniqueId}-gold)`"
          :stroke-width="strokeWidth"
          stroke-linejoin="miter"
          :filter="`url(#${uniqueId}-glow)`"
        />
        <path
          :d="innerDiamondPath"
          :fill="`url(#${uniqueId}-gold)`"
          opacity="0.4"
        />
      </g>
    </svg>
  </div>
</template>

<style scoped>
.bubble-frame-container {
  pointer-events: none;
}

.bubble-frame {
  overflow: visible;
}

.diamond-ornament {
  animation: diamond-fade-in 0.2s ease-out;
}

@keyframes diamond-fade-in {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
